import { Check } from 'lucide-react';
import { TEAL_TEXT } from '../constants';
import type { RunSummaryVM, OptimizationVM, ActionCardStatus, OptimizationTarget } from '../types';
import { OptimizationCard } from '../components/optimization-card';

// Run Summary card + Priority optimizations grid (DC §5 / §6). Renders only when
// a completed run exists (the container gates on hasRunData && !isAnalyzing).
export function RunSummarySection({
  summary,
  optimizations,
  statusFor,
  onAdd,
  onView,
  onNavigate,
  gated = false,
}: {
  summary: RunSummaryVM;
  optimizations: OptimizationVM[];
  statusFor: (id: string) => ActionCardStatus;
  onAdd: (opt: OptimizationVM) => void;
  onView: (opt: OptimizationVM) => void;
  onNavigate: (target: OptimizationTarget) => void;
  gated?: boolean;
}) {
  const hasText = !!summary.lede || !!summary.detail;

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderLeft: '4px solid var(--alpha-teal)',
        borderRadius: 12,
        boxShadow: '0 12px 32px -18px rgba(13,13,15,.14)',
        overflow: 'hidden',
        marginBottom: 26,
      }}
    >
      <div style={{ padding: '24px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 9,
              background: 'var(--alpha-teal-10)',
              color: TEAL_TEXT,
            }}
          >
            <Check style={{ width: 17, height: 17 }} />
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: TEAL_TEXT,
            }}
          >
            Run summary
          </span>
          {summary.score != null && (
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: 'var(--foreground)',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-.02em',
                }}
              >
                {summary.score}
              </span>
              <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>/10 overall</span>
            </span>
          )}
        </div>

        {hasText ? (
          <>
            {summary.lede && (
              <p style={{ fontSize: 15, lineHeight: 1.65, color: 'var(--foreground)', margin: '0 0 8px', overflowWrap: 'anywhere' }}>
                {summary.lede}
              </p>
            )}
            {summary.detail && (
              <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--muted-foreground)', margin: '0 0 22px', overflowWrap: 'anywhere' }}>
                {summary.detail}
              </p>
            )}
          </>
        ) : (
          <p style={{ fontSize: 14, color: 'var(--muted-foreground)', margin: '0 0 22px' }}>
            Summary isn't available for this run yet.
          </p>
        )}

        {optimizations.length > 0 && (
          <>
            <div style={{ height: 1, background: 'var(--border)', marginBottom: 20 }} />
            <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--foreground)', margin: '0 0 14px' }}>
              Priority optimizations
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {optimizations.map((opt) => (
                <OptimizationCard
                  key={opt.id}
                  opt={opt}
                  status={statusFor(opt.id)}
                  onAdd={onAdd}
                  onView={onView}
                  onNavigate={onNavigate}
                  gated={gated}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
